// src/modules/leads/route-planner/useRoutePlanner.js
import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { getLeads } from '../leadService';
import { fetchAcceptedServiceMap, leadMatchesServiceFilter } from '../leadEstimateServiceFilter';
import { findLeadsAlongRoute, generateGoogleMapsUrl, extractRouteInfo } from './routeUtils';

const MAX_WAYPOINTS = 23; // Google Directions API limit (25 total - origin - destination)

// Pipeline stages (kept in sync with LeadPipeline.js)
const PIPELINE_STAGES = [
  { key: 'new', label: 'New' },
  { key: 'first_contact', label: 'First Contact' },
  { key: 'quote_sent', label: 'Quote Sent' },
  { key: 'quote_opened', label: 'Quote Opened' },
  { key: 'follow_up_1', label: '1st Follow Up' },
  { key: 'follow_up_2', label: '2nd Follow Up' },
  { key: 'follow_up_3', label: '3rd Follow Up' },
  { key: 'won', label: 'Won' },
  { key: 'lost', label: 'Lost' },
];

const useRoutePlanner = (isGoogleLoaded) => {
  // ─── Step tracking ──────────────────────────────────────────────────────
  const [currentStep, setCurrentStep] = useState(1);

  // ─── Departure (Point A) ────────────────────────────────────────────────
  const [departure, setDepartureState] = useState(null); // { lat, lng, address }
  const [departureLoading, setDepartureLoading] = useState(false);
  const [departureError, setDepartureError] = useState(null);

  // ─── Destination (Point X) ──────────────────────────────────────────────
  const [destination, setDestinationState] = useState(null); // { lat, lng, address }
  const [isRoundTrip, setIsRoundTrip] = useState(false);

  // ─── Lead data ──────────────────────────────────────────────────────────
  const [allLeads, setAllLeads] = useState([]);
  const [leadsLoading, setLeadsLoading] = useState(true);

  // ─── Filters ────────────────────────────────────────────────────────────
  const [searchQuery, setSearchQuery] = useState('');
  const [cityFilter, setCityFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [pipelineFilter, setPipelineFilter] = useState('all');
  const [dateRange, setDateRange] = useState('30');
  const [serviceFilter, setServiceFilter] = useState([]); // [] = all; ANY-match on accepted-quote services
  const [serviceMap, setServiceMap] = useState(new Map());
  const [availableServices, setAvailableServices] = useState([]);

  // ─── Route waypoints ────────────────────────────────────────────────────
  const [selectedLeadIds, setSelectedLeadIds] = useState(new Set());
  const [routeStops, setRouteStops] = useState([]); // Ordered after optimization
  const [routeResponse, setRouteResponse] = useState(null);
  const [routeInfo, setRouteInfo] = useState(null);
  const [isOptimizing, setIsOptimizing] = useState(false);
  const [routeError, setRouteError] = useState(null);
  const [routeDirty, setRouteDirty] = useState(false); // true when leads changed after optimization

  // ─── Base route (A→B, no waypoints, for along-route search) ─────────────
  const [baseRouteResponse, setBaseRouteResponse] = useState(null);
  const [isCalculatingBaseRoute, setIsCalculatingBaseRoute] = useState(false);

  // ─── Along route ────────────────────────────────────────────────────────
  const [alongRouteDistance, setAlongRouteDistance] = useState(15);
  const [alongRouteLeads, setAlongRouteLeads] = useState([]);
  const [alongRouteFilters, setAlongRouteFilters] = useState({ status: 'all' });
  const [showAlongRoute, setShowAlongRoute] = useState(false);
  const [isSearchingAlongRoute, setIsSearchingAlongRoute] = useState(false);

  // ─── Share ──────────────────────────────────────────────────────────────
  const [googleMapsUrl, setGoogleMapsUrl] = useState(null);
  const [copied, setCopied] = useState(false);

  // ─── Refs ───────────────────────────────────────────────────────────────
  const directionsServiceRef = useRef(null);

  // ─── Fetch leads on mount ───────────────────────────────────────────────
  useEffect(() => {
    const fetchLeads = async () => {
      try {
        setLeadsLoading(true);
        const leads = await getLeads();
        setAllLeads(Array.isArray(leads) ? leads : []);
      } catch (err) {
        console.error('Error fetching leads:', err);
        setAllLeads([]);
      } finally {
        setLeadsLoading(false);
      }
    };
    fetchLeads();
  }, []);

  // ─── Load accepted-quote services (for the service-type filter) ─────────
  useEffect(() => {
    let active = true;
    fetchAcceptedServiceMap().then(({ serviceMap: map, availableServices: services }) => {
      if (!active) return;
      setServiceMap(map);
      setAvailableServices(services);
    });
    return () => { active = false; };
  }, []);

  // ─── "Use My Location" — on-demand GPS, not automatic ──────────────────
  const useMyLocation = useCallback(() => {
    if (!navigator.geolocation) {
      setDepartureError('Geolocation is not supported by your browser');
      return;
    }

    setDepartureLoading(true);
    setDepartureError(null);

    const reverseGeocode = (lat, lng, cb) => {
      if (window.google?.maps?.Geocoder) {
        const geocoder = new window.google.maps.Geocoder();
        geocoder.geocode({ location: { lat, lng } }, (results, status) => {
          cb(status === 'OK' && results?.[0] ? results[0].formatted_address : `${lat.toFixed(6)}, ${lng.toFixed(6)}`);
        });
      } else {
        cb(`${lat.toFixed(6)}, ${lng.toFixed(6)}`);
      }
    };

    const onPosition = (position) => {
      const { latitude, longitude } = position.coords;
      reverseGeocode(latitude, longitude, (address) => {
        setDepartureState({ lat: latitude, lng: longitude, address });
        setDepartureLoading(false);
        setDepartureError(null);
        setCurrentStep(2);
      });
    };

    const onError = (err) => {
      setDepartureLoading(false);
      const msg = err.code === 1
        ? 'Location access denied. Please enter your address instead.'
        : err.code === 3
        ? 'Location request timed out. Try again or enter an address.'
        : 'Could not detect location. Please enter an address.';
      setDepartureError(msg);
    };

    // Try low-accuracy first (fast on iOS), fallback to high-accuracy
    navigator.geolocation.getCurrentPosition(
      onPosition,
      (lowAccErr) => {
        if (lowAccErr.code === 1) { onError(lowAccErr); return; }
        navigator.geolocation.getCurrentPosition(onPosition, onError,
          { enableHighAccuracy: true, timeout: 20000, maximumAge: 300000 });
      },
      { enableHighAccuracy: false, timeout: 10000, maximumAge: 300000 }
    );
  }, []);

  // ─── Initialize DirectionsService ───────────────────────────────────────
  useEffect(() => {
    if (isGoogleLoaded && window.google?.maps) {
      directionsServiceRef.current = new window.google.maps.DirectionsService();
    }
  }, [isGoogleLoaded]);

  // ─── The effective destination (accounts for round trip) ────────────────
  const effectiveDestination = useMemo(() => {
    if (isRoundTrip && departure) return departure;
    return destination;
  }, [isRoundTrip, departure, destination]);

  // ─── Calculate base route A→B whenever both points are set ──────────────
  //     This is the route used for "Find Leads Along Route"
  //     Only fires when start ≠ finish (not round trip)
  useEffect(() => {
    if (!departure || !effectiveDestination || !directionsServiceRef.current) {
      setBaseRouteResponse(null);
      return;
    }

    // Skip base route for round trip (same point → no meaningful route line)
    if (isRoundTrip) {
      setBaseRouteResponse(null);
      return;
    }

    setIsCalculatingBaseRoute(true);

    const request = {
      origin: new window.google.maps.LatLng(departure.lat, departure.lng),
      destination: new window.google.maps.LatLng(effectiveDestination.lat, effectiveDestination.lng),
      travelMode: window.google.maps.TravelMode.DRIVING,
    };

    directionsServiceRef.current.route(request, (response, status) => {
      if (status === 'OK') {
        setBaseRouteResponse(response);
      } else {
        console.warn('Base route calculation failed:', status);
        setBaseRouteResponse(null);
      }
      setIsCalculatingBaseRoute(false);
    });
  }, [departure, effectiveDestination, isRoundTrip]);

  // ─── Derived: date-filtered leads ───────────────────────────────────────
  const dateFilteredLeads = useMemo(() => {
    if (dateRange === 'all') return allLeads;
    const days = parseInt(dateRange);
    const cutoff = new Date();
    cutoff.setDate(cutoff.getDate() - days);
    return allLeads.filter(lead => new Date(lead.createdAt) >= cutoff);
  }, [allLeads, dateRange]);

  // ─── Derived: available cities and statuses ─────────────────────────────
  const availableCities = useMemo(() => {
    const cities = new Set(dateFilteredLeads.map(l => l.city).filter(Boolean));
    return Array.from(cities).sort();
  }, [dateFilteredLeads]);

  const availableStatuses = useMemo(() => {
    const statuses = new Set(dateFilteredLeads.map(lead => {
      return lead.status?.status_name || lead.serviceRequests?.[0]?.LeadStatus?.status_name || 'No Status';
    }));
    return Array.from(statuses).sort();
  }, [dateFilteredLeads]);

  // Pipeline stages present in the current (date-filtered) lead set, in the
  // canonical PIPELINE_STAGES order. Leads without an explicit stage are
  // treated as 'new' (matching LeadList/LeadDetails behavior).
  const availablePipelineStages = useMemo(() => {
    const present = new Set(dateFilteredLeads.map(l => l.pipeline_stage || 'new'));
    return PIPELINE_STAGES.filter(s => present.has(s.key));
  }, [dateFilteredLeads]);

  // ─── Derived: filtered leads ────────────────────────────────────────────
  const filteredLeads = useMemo(() => {
    let result = dateFilteredLeads;

    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      result = result.filter(lead =>
        lead.name?.toLowerCase().includes(q) ||
        lead.city?.toLowerCase().includes(q) ||
        lead.phone?.includes(q) ||
        lead.email?.toLowerCase().includes(q)
      );
    }

    if (cityFilter !== 'all') {
      result = result.filter(lead => lead.city === cityFilter);
    }

    if (statusFilter !== 'all') {
      result = result.filter(lead => {
        const name = lead.status?.status_name || lead.serviceRequests?.[0]?.LeadStatus?.status_name || 'No Status';
        return name === statusFilter;
      });
    }

    if (pipelineFilter !== 'all') {
      result = result.filter(lead => (lead.pipeline_stage || 'new') === pipelineFilter);
    }

    if (serviceFilter.length > 0) {
      result = result.filter(lead => leadMatchesServiceFilter(lead, serviceFilter, serviceMap));
    }

    return result;
  }, [dateFilteredLeads, searchQuery, cityFilter, statusFilter, pipelineFilter, serviceFilter, serviceMap]);

  // ─── Derived: mappable leads (with coords) ─────────────────────────────
  const mappableLeads = useMemo(() => {
    return filteredLeads.filter(l => l.latitude && l.longitude);
  }, [filteredLeads]);

  // ─── Derived: selected leads array ─────────────────────────────────────
  const selectedLeads = useMemo(() => {
    return allLeads.filter(l => selectedLeadIds.has(l.id));
  }, [allLeads, selectedLeadIds]);

  // ─── Can use "Find Along Route"? ────────────────────────────────────────
  //     Available when: start & finish are set AND they are different points
  const canFindAlongRoute = useMemo(() => {
    return !!departure && !!effectiveDestination && !isRoundTrip && !!baseRouteResponse;
  }, [departure, effectiveDestination, isRoundTrip, baseRouteResponse]);

  // ─── Actions ────────────────────────────────────────────────────────────

  const setDeparture = useCallback((point) => {
    setDepartureState(point);
    setDepartureError(null);
    if (point && !destination && !isRoundTrip) {
      setCurrentStep(2);
    } else if (point && (destination || isRoundTrip)) {
      setCurrentStep(3);
    }
  }, [destination, isRoundTrip]);

  const setDestination = useCallback((address, lat, lng) => {
    setDestinationState({ lat, lng, address });
    setIsRoundTrip(false);
    setCurrentStep(3);
  }, []);

  const setRoundTrip = useCallback((value) => {
    setIsRoundTrip(value);
    if (value && departure) {
      // Round trip: destination is same as departure
      setDestinationState(null); // Clear separate destination
      setCurrentStep(3);
    }
    // Reset route state
    setRouteResponse(null);
    setRouteInfo(null);
    setRouteStops([]);
    setGoogleMapsUrl(null);
    setAlongRouteLeads([]);
    setShowAlongRoute(false);
  }, [departure]);

  const addLeadToRoute = useCallback((leadId) => {
    setSelectedLeadIds(prev => {
      const next = new Set(prev);
      next.add(leadId);
      return next;
    });
    // If a route is already calculated, mark dirty instead of clearing
    if (routeResponse) {
      setRouteDirty(true);
    }
  }, [routeResponse]);

  const removeLeadFromRoute = useCallback((leadId) => {
    setSelectedLeadIds(prev => {
      const next = new Set(prev);
      next.delete(leadId);
      return next;
    });
    if (routeResponse) {
      setRouteDirty(true);
    }
  }, [routeResponse]);

  const toggleLeadInRoute = useCallback((leadId) => {
    setSelectedLeadIds(prev => {
      const next = new Set(prev);
      if (next.has(leadId)) {
        next.delete(leadId);
      } else {
        next.add(leadId);
      }
      return next;
    });
    if (routeResponse) {
      setRouteDirty(true);
    }
  }, [routeResponse]);

  const optimizeRoute = useCallback(async () => {
    if (!departure || !effectiveDestination || selectedLeadIds.size === 0) return;
    if (!directionsServiceRef.current) return;

    setIsOptimizing(true);
    setRouteError(null);

    try {
      let leadsForRoute = allLeads.filter(l =>
        selectedLeadIds.has(l.id) && l.latitude && l.longitude
      );

      // Enforce waypoint limit
      if (leadsForRoute.length > MAX_WAYPOINTS) {
        leadsForRoute.sort((a, b) => {
          const distA = Math.pow(a.latitude - departure.lat, 2) + Math.pow(a.longitude - departure.lng, 2);
          const distB = Math.pow(b.latitude - departure.lat, 2) + Math.pow(b.longitude - departure.lng, 2);
          return distA - distB;
        });
        leadsForRoute = leadsForRoute.slice(0, MAX_WAYPOINTS);
      }

      const waypoints = leadsForRoute.map(lead => ({
        location: new window.google.maps.LatLng(parseFloat(lead.latitude), parseFloat(lead.longitude)),
        stopover: true,
      }));

      const request = {
        origin: new window.google.maps.LatLng(departure.lat, departure.lng),
        destination: new window.google.maps.LatLng(effectiveDestination.lat, effectiveDestination.lng),
        waypoints,
        optimizeWaypoints: true,
        travelMode: window.google.maps.TravelMode.DRIVING,
      };

      const result = await new Promise((resolve, reject) => {
        directionsServiceRef.current.route(request, (response, status) => {
          if (status === 'OK') resolve(response);
          else reject(new Error(`Directions request failed: ${status}`));
        });
      });

      // Reorder stops based on optimized waypoint order
      const waypointOrder = result.routes[0].waypoint_order;
      const orderedStops = waypointOrder.map(i => leadsForRoute[i]);

      setRouteResponse(result);
      setRouteStops(orderedStops);
      setRouteInfo(extractRouteInfo(result));
      setRouteDirty(false);
      setCurrentStep(4);

      // Generate share URL
      const url = generateGoogleMapsUrl(departure, effectiveDestination, orderedStops);
      setGoogleMapsUrl(url);

    } catch (err) {
      console.error('Route optimization failed:', err);
      setRouteError(err.message || 'Failed to calculate route');
    } finally {
      setIsOptimizing(false);
    }
  }, [departure, effectiveDestination, selectedLeadIds, allLeads]);

  // ─── "Find Along Route" uses the BASE route (A→B, no waypoints) ─────────
  const searchAlongRoute = useCallback(() => {
    // Use the optimized route if available, otherwise the base A→B route
    const routeToUse = routeResponse || baseRouteResponse;
    if (!routeToUse?.routes?.[0]?.overview_path) {
      setAlongRouteLeads([]);
      setShowAlongRoute(true); // Show empty state message
      return;
    }

    if (!window.google?.maps?.geometry) {
      console.error('Google Maps Geometry library not loaded');
      return;
    }

    setIsSearchingAlongRoute(true);

    try {
      const polylinePath = routeToUse.routes[0].overview_path;
      const found = findLeadsAlongRoute(
        polylinePath,
        allLeads,
        alongRouteDistance,
        selectedLeadIds,
        alongRouteFilters
      );

      setAlongRouteLeads(found);
      setShowAlongRoute(true);
    } catch (err) {
      console.error('Along-route search failed:', err);
      setAlongRouteLeads([]);
      setShowAlongRoute(true);
    } finally {
      setIsSearchingAlongRoute(false);
    }
  }, [routeResponse, baseRouteResponse, allLeads, alongRouteDistance, selectedLeadIds, alongRouteFilters]);

  const addAlongRouteLeadToRoute = useCallback((leadId) => {
    addLeadToRoute(leadId);
    setAlongRouteLeads(prev => prev.filter(l => l.id !== leadId));
  }, [addLeadToRoute]);

  const copyToClipboard = useCallback(async () => {
    if (!googleMapsUrl) return;
    try {
      await navigator.clipboard.writeText(googleMapsUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      const textArea = document.createElement('textarea');
      textArea.value = googleMapsUrl;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    }
  }, [googleMapsUrl]);

  const shareViaWebShare = useCallback(async () => {
    if (!googleMapsUrl || !navigator.share) return;
    try {
      await navigator.share({
        title: 'Route Plan - Mr. Patch Asphalt',
        text: `Route with ${routeStops.length} stops`,
        url: googleMapsUrl,
      });
    } catch (err) {
      // User cancelled or share failed
    }
  }, [googleMapsUrl, routeStops]);

  const openInMaps = useCallback(() => {
    if (googleMapsUrl) {
      window.open(googleMapsUrl, '_blank');
    }
  }, [googleMapsUrl]);

  const goToStep = useCallback((step) => {
    setCurrentStep(step);
  }, []);

  const goToShare = useCallback(() => {
    setCurrentStep(5);
  }, []);

  const resetRoute = useCallback(() => {
    setSelectedLeadIds(new Set());
    setRouteStops([]);
    setRouteResponse(null);
    setRouteInfo(null);
    setRouteError(null);
    setRouteDirty(false);
    setAlongRouteLeads([]);
    setShowAlongRoute(false);
    setGoogleMapsUrl(null);
    setCopied(false);
    setCurrentStep(departure && effectiveDestination ? 3 : departure ? 2 : 1);
  }, [departure, effectiveDestination]);

  const startOver = useCallback(() => {
    setDepartureState(null);
    setDepartureLoading(false);
    setDepartureError(null);
    setDestinationState(null);
    setIsRoundTrip(false);
    setSelectedLeadIds(new Set());
    setRouteStops([]);
    setRouteResponse(null);
    setRouteInfo(null);
    setRouteError(null);
    setRouteDirty(false);
    setBaseRouteResponse(null);
    setAlongRouteLeads([]);
    setShowAlongRoute(false);
    setGoogleMapsUrl(null);
    setCopied(false);
    setSearchQuery('');
    setCityFilter('all');
    setStatusFilter('all');
    setServiceFilter([]);
    setDateRange('30');
    setAlongRouteDistance(15);
    setAlongRouteFilters({ status: 'all' });
    setCurrentStep(1);
  }, []);

  return {
    // Step
    currentStep,
    goToStep,
    goToShare,

    // Departure
    departure,
    departureLoading,
    departureError,
    setDeparture,
    useMyLocation,

    // Destination
    destination,
    effectiveDestination,
    isRoundTrip,
    setDestination,
    setRoundTrip,

    // Leads
    allLeads,
    leadsLoading,
    filteredLeads,
    mappableLeads,
    selectedLeads,
    selectedLeadIds,
    availableCities,
    availableStatuses,
    availablePipelineStages,
    availableServices,

    // Filters
    searchQuery,
    setSearchQuery,
    cityFilter,
    setCityFilter,
    statusFilter,
    setStatusFilter,
    pipelineFilter,
    setPipelineFilter,
    serviceFilter,
    setServiceFilter,
    dateRange,
    setDateRange,

    // Route
    routeStops,
    routeResponse,
    routeInfo,
    isOptimizing,
    routeError,
    routeDirty,
    optimizeRoute,
    addLeadToRoute,
    removeLeadFromRoute,
    toggleLeadInRoute,

    // Base route & along route
    baseRouteResponse,
    isCalculatingBaseRoute,
    canFindAlongRoute,
    alongRouteDistance,
    setAlongRouteDistance,
    alongRouteLeads,
    alongRouteFilters,
    setAlongRouteFilters,
    showAlongRoute,
    setShowAlongRoute,
    isSearchingAlongRoute,
    searchAlongRoute,
    addAlongRouteLeadToRoute,

    // Share
    googleMapsUrl,
    copied,
    copyToClipboard,
    shareViaWebShare,
    openInMaps,
    resetRoute,
    startOver,
  };
};

export default useRoutePlanner;
